import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const colors = {
  mainBlue: "#061941",
  xanthous: "#F2AF29",
  isabelline: "#F4F3EE",
  carrieban: "#3C6E71",
  muted: "#6B7280",
};

function DirectMessages() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageContent, setMessageContent] = useState("");
  const [loading, setLoading] = useState(true);
  const messagesContainerRef = useRef(null);
  const nearBottomRef = useRef(true);
  const justSentRef = useRef(false);
  const prevLengthRef = useRef(0);

  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const currentUserId = userData.id || userData._id;
  const token = userData.token;

  const hasUnseen = (conversation) => {
    const lastMsg = conversation?.lastMessage;
    if (!lastMsg) return false;
    const fromOtherUser =
      lastMsg.senderId && lastMsg.senderId.toString() !== currentUserId;
    const unseenByTimestamp = !lastMsg.seenAt ||
      new Date(lastMsg.seenAt) < new Date(lastMsg.sentAt || lastMsg.createdAt || lastMsg.sentAt);
    return fromOtherUser && unseenByTimestamp;
  };

  const fetchConversations = async (withLoading = false) => {
    try {
      if (withLoading) setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(res.data.conversations || []);
    } catch (err) {
      console.error("Error fetching conversations:", err);
    } finally {
      if (withLoading) setLoading(false);
    }
  };

  // Fetch conversations initially and poll for updates
  useEffect(() => {
    if (!token) return;
    fetchConversations(true);
    const interval = setInterval(() => fetchConversations(false), 4000);
    return () => clearInterval(interval);
  }, [token]);

  const markConversationSeen = async (conversationId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/${conversationId}/mark-seen`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error("Error marking conversation as seen:", err);
    }
  };

  // Fetch messages for selected conversation
  useEffect(() => {
    if (!selectedConversation) return undefined;

    const fetchMessages = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/messages/${selectedConversation._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const fetched = res.data.messages || [];
        setMessages(fetched);

        // If any incoming messages are unseen, optimistically mark them as seen
        const unseenIncoming = fetched.filter(
          (m) =>
            m.sender._id !== currentUserId && (!m.seenAt || new Date(m.seenAt) < new Date(m.sentAt))
        );
        if (unseenIncoming.length > 0) {
          const nowIso = new Date().toISOString();
          // Optimistic local update
          setMessages((prev) =>
            prev.map((m) =>
              m.sender._id !== currentUserId && (!m.seenAt || new Date(m.seenAt) < new Date(m.sentAt))
                ? { ...m, seenAt: nowIso }
                : m
            )
          );
          markConversationSeen(selectedConversation._id);
          // Also reflect in conversations list
          setConversations((prev) =>
            prev.map((c) =>
              c._id === selectedConversation._id && c.lastMessage
                ? {
                    ...c,
                    lastMessage: { ...c.lastMessage, seenAt: nowIso },
                  }
                : c
            )
          );
          // Optimistically decrement global badge
          window.dispatchEvent(
            new CustomEvent("dm-unread-updated", {
              detail: { delta: -unseenIncoming.length },
            })
          );
        }

        // Keep the conversation list in sync with latest message
        const last = fetched[fetched.length - 1];
        if (last) {
          setConversations((prev) =>
            prev.map((c) =>
              c._id === selectedConversation._id
                ? {
                    ...c,
                    lastMessage: {
                      _id: last._id,
                      content: last.content,
                      senderId: last.sender._id,
                      recipientId: last.recipient._id,
                      sentAt: last.sentAt,
                      seenAt: last.seenAt,
                    },
                    lastMessageTime: last.sentAt,
                  }
                : c
            )
          );
        }
      } catch (err) {
        console.error("Error fetching messages:", err);
      }
    };

    fetchMessages();
    // Poll for new messages every 2 seconds
    const interval = setInterval(fetchMessages, 2000);
    return () => clearInterval(interval);
  }, [selectedConversation, token, currentUserId]);

  // Auto-scroll behavior on messages change
  useEffect(() => {
    const newLength = messages.length;
    const hadGrowth = newLength > prevLengthRef.current;
    const shouldScroll = justSentRef.current || nearBottomRef.current || prevLengthRef.current === 0;

    if (hadGrowth && shouldScroll) {
      // smooth when already mounted; instant on first load
      const behavior = prevLengthRef.current === 0 ? "auto" : "smooth";
      scrollToBottom(behavior);
    }

    justSentRef.current = false;
    prevLengthRef.current = newLength;
  }, [messages]);

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    nearBottomRef.current = true;
    prevLengthRef.current = 0;
    // Optimistic dot removal
    setConversations((prev) =>
      prev.map((c) =>
        c._id === conversation._id && c.lastMessage
          ? { ...c, lastMessage: { ...c.lastMessage, seenAt: new Date().toISOString() } }
          : c
      )
    );
    markConversationSeen(conversation._id);
  };

  const isNearBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    const threshold = 100;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distanceFromBottom <= threshold;
  };

  const scrollToBottom = (behavior = "smooth") => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  // Send a message
  const handleSendMessage = async () => {
    if (!messageContent.trim() || !selectedConversation) return;

    justSentRef.current = true; // force scroll after sending

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages`,
        {
          recipientId: selectedConversation.otherUser._id,
          content: messageContent,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages([...messages, res.data.message]);
      setMessageContent("");
    } catch (err) {
      console.error("Error sending message:", err);
      alert("Failed to send message");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          padding: "40px",
          textAlign: "center",
          backgroundColor: colors.isabelline,
          minHeight: "100vh",
        }}
      >
        <p>Loading conversations...</p>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        height: "calc(100vh - 80px)",
        backgroundColor: colors.isabelline,
      }}
    >
      {/* Conversations List */}
      <div
        style={{
          width: "300px",
          backgroundColor: "white",
          borderRight: `1px solid ${colors.mainBlue}20`,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            padding: "16px",
            borderBottom: `1px solid ${colors.mainBlue}20`,
          }}
        >
          <h2 style={{ margin: "0", color: colors.mainBlue, fontSize: "18px" }}>
            Messages
          </h2>
        </div>

        {/* Conversations List */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {conversations.length === 0 ? (
            <p
              style={{
                padding: "16px",
                color: colors.muted,
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              No conversations yet
            </p>
          ) : (
            conversations.map((conversation) => (
              <div
                key={conversation._id}
                onClick={() => handleSelectConversation(conversation)}
                style={{
                  padding: "12px 16px",
                  borderBottom: `1px solid ${colors.mainBlue}10`,
                  cursor: "pointer",
                  backgroundColor:
                    selectedConversation?._id === conversation._id
                      ? colors.isabelline
                      : "white",
                  transition: "background-color 0.2s",
                }}
                onMouseOver={(e) => {
                  if (selectedConversation?._id !== conversation._id) {
                    e.currentTarget.style.backgroundColor = colors.isabelline;
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedConversation?._id !== conversation._id) {
                    e.currentTarget.style.backgroundColor = "white";
                  }
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <p
                    style={{
                      margin: "0",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: colors.mainBlue,
                    }}
                  >
                    {conversation.otherUser.name}
                  </p>
                  {hasUnseen(conversation) && (
                    <span
                      aria-label="unread messages"
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: "#3b82f6",
                        display: "inline-block",
                      }}
                    />
                  )}
                </div>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "12px",
                    color: colors.muted,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {conversation.lastMessage?.content || conversation.lastMessage || "No messages yet"}
                </p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Messages View */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "white",
        }}
      >
        {!selectedConversation ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.muted,
            }}
          >
            <p>To start a message, click the 'contact' button when
               viewing the Professor Pool or My Applications page! </p>
          </div>
        ) : (
          <>
            {/* Messages Header */}
            <div
              style={{
                padding: "16px",
                borderBottom: `1px solid ${colors.mainBlue}20`,
                backgroundColor: colors.mainBlue,
                color: "white",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "18px", color: "white" }}>
                {selectedConversation.otherUser.name}
              </h2>
              <p
                style={{ margin: "4px 0 0 0", fontSize: "12px", opacity: 0.9 }}
              >
                {selectedConversation.otherUser.email}
              </p>
            </div>

            {/* Messages List */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
              ref={messagesContainerRef}
              onScroll={() => {
                nearBottomRef.current = isNearBottom();
              }}
            >
              {messages.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: colors.muted,
                    marginTop: "20px",
                  }}
                >
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      justifyContent:
                        msg.sender._id === currentUserId
                          ? "flex-end"
                          : "flex-start",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "60%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        backgroundColor:
                          msg.sender._id === currentUserId
                            ? colors.mainBlue
                            : colors.isabelline,
                        color:
                          msg.sender._id === currentUserId
                            ? "white"
                            : colors.mainBlue,
                        wordWrap: "break-word",
                      }}
                    >
                      <p style={{ margin: 0, fontSize: "14px" }}>
                        {msg.content}
                      </p>
                      <p
                        style={{
                          margin: "4px 0 0 0",
                          fontSize: "11px",
                          opacity: 0.7,
                        }}
                      >
                        {new Date(msg.sentAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Message Input */}
            <div
              style={{
                padding: "12px 16px",
                borderTop: `1px solid ${colors.mainBlue}20`,
                display: "flex",
                gap: "8px",
              }}
            >
              <input
                type="text"
                placeholder="Type a message..."
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSendMessage();
                  }
                }}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  border: `1px solid ${colors.mainBlue}30`,
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                }}
              />
              <button
                onClick={handleSendMessage}
                disabled={!messageContent.trim()}
                style={{
                  padding: "10px 16px",
                  backgroundColor: colors.mainBlue,
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: messageContent.trim() ? "pointer" : "not-allowed",
                  fontSize: "14px",
                  fontWeight: 600,
                  opacity: messageContent.trim() ? 1 : 0.5,
                }}
              >
                Send
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DirectMessages;
